<template>
  <div>
    <cl-table-wrapper class-name="tw-bg-primary-grey-25">
      <template #before>
        <div class="tw-flex tw-items-center tw-justify-between tw-pb-2">
          <div>
            <label
              for="msp-email-security-customers-table-search"
              class="tw-sr-only"
              >Search
            </label>
            <div class="tw-w-[320px]">
              <cl-input-search
                id="msp-email-security-customers-table-search"
                :name="'msp-email-security-customers-table-search'"
                test-id="msp-email-security-customers-table-search"
                :placeholder="$t('search')"
                :size="'medium'"
                :value="filterState"
                :readonly="loading"
                @update:value="debounceSearch"
              />
            </div>
          </div>
          <div
            class="tw-col-span-6 max-md:tw-col-span-12 tw-flex tw-items-center tw-justify-end"
          >
            <div class="tw-inline-flex">
              <cl-table-column-visibility-toggle
                class="tw-relative"
                data-test-id="columns-visibility-button"
              >
                <div
                  v-for="column in columnsToggleList"
                  :key="column.id"
                  class="tw-mb-2"
                >
                  <label class="tw-flex tw-items-center tw-gap-2">
                    <cl-input-checkbox
                      :id="column.id"
                      :checked="column.getIsVisible()"
                      :disabled="
                        column.getIsVisible() &&
                        table.getVisibleLeafColumns().length <= 2
                      "
                      :indeterminate="false"
                      :name="column.id"
                      @change="toggleColumnVisibility(column)"
                    />
                    {{ column.columnDef.header }}
                  </label>
                </div>
              </cl-table-column-visibility-toggle>
            </div>
          </div>
        </div>
      </template>
      <template #table>
        <cl-table>
          <cl-thead>
            <cl-tr
              v-for="headerGroup in table.getHeaderGroups()"
              :key="headerGroup.id"
              :table-target="'thead'"
              class="tw-flex tw-w-full"
            >
              <cl-th
                v-for="header in headerGroup.headers"
                :key="header.id"
                :col-span="header.colSpan"
                class="tw-block"
                :class="{
                  'tw-select-none': header.column.getCanSort(),
                  'tw-sticky tw-right-0 tw-bg-[inherit] tw-ml-auto':
                    header.column.getIsPinned(),
                }"
                scope="col"
                :style="{ width: `${header.getSize()}px` }"
              >
                <div class="tw-flex tw-h-7 tw-items-center tw-justify-between">
                  <button
                    v-if="header.column.getCanSort()"
                    class="tw-flex tw-items-center tw-h-7"
                    :class="{
                      'tw-text-primary-grey-300': loading,
                    }"
                    :data-test-id="`msp-email-security-customers-table-header-${header.id}`"
                    :disabled="loading"
                    @click="header.column.getToggleSortingHandler()?.($event)"
                  >
                    <template v-if="!header.isPlaceholder">
                      <FlexRender
                        :render="header.column.columnDef.header"
                        :props="header.getContext()"
                      />
                      <span
                        v-if="header.column.columnDef.meta?.headerTooltip"
                        v-tippy="{
                          content: header.column.columnDef.meta?.headerTooltip,
                          placement: 'top',
                        }"
                        class="tw-text-primary-grey-200 tw-ml-2"
                        @click.stop
                      >
                        <CbIconInformation :width="18" :height="18" />
                      </span>
                      <cl-th-sorting
                        :sort-direction="header.column.getIsSorted()"
                      />
                    </template>
                  </button>
                  <div v-else>
                    <template v-if="!header.isPlaceholder">
                      <FlexRender
                        :render="header.column.columnDef.header"
                        :props="header.getContext()"
                      />
                      <span
                        v-if="header.column.columnDef.meta?.headerTooltip"
                        v-tippy="{
                          content: header.column.columnDef.meta?.headerTooltip,
                          placement: 'top',
                        }"
                        class="tw-text-primary-grey-200 tw-ml-2"
                        @click.stop
                      >
                        <CbIconInformation :width="18" :height="18" />
                      </span>
                    </template>
                  </div>
                </div>
              </cl-th>
            </cl-tr>
          </cl-thead>
          <cl-tbody v-if="loading">
            <cl-tr-skeleton :rows="skeletonArray" />
          </cl-tbody>
          <cl-tbody v-else-if="!customersList.length">
            <cl-tr zebra>
              <cl-td
                colspan="100%"
                :class-name="'tw-text-center tw-pl-0 tw-pr-0 tw-pb-0'"
              >
                <t-empty>
                  <template #image>
                    <component :is="emptyState.icon" />
                  </template>
                  <template #title>
                    {{ emptyState.title }}
                  </template>
                  <template #description>
                    {{ emptyState.description }}
                  </template>
                </t-empty>
              </cl-td>
            </cl-tr>
          </cl-tbody>
          <cl-tbody v-else data-clarity-mask="true">
            <cl-tr
              v-for="(row, index) in table.getRowModel().rows"
              :key="row.id"
              :zebra="index % 2 === 0"
              class="tw-flex tw-w-full"
            >
              <cl-td
                v-for="cell in row.getVisibleCells()"
                :key="cell.id"
                class="tw-overflow-hidden tw-whitespace-nowrap tw-text-ellipsis"
                :class="{
                  'tw-sticky tw-right-0 tw-bg-[inherit] tw-ml-auto':
                    cell.column.getIsPinned(),
                }"
                :style="{ width: `${cell.column.getSize()}px` }"
              >
                <FlexRender
                  :render="cell.column.columnDef.cell"
                  :props="cell.getContext()"
                />
              </cl-td>
            </cl-tr>
          </cl-tbody>
        </cl-table>
      </template>
      <template #after>
        <div class="tw-relative">
          <div v-if="loading"><cl-skeleton-row /></div>
          <div v-else class="tw-flex tw-justify-between tw-items-baseline">
            <cl-table-page-size
              :placeholder="$t('rows-per-page')"
              :page-size="table.getState().pagination.pageSize"
              :page-sizes="[10, 25, 50]"
              :handle-page-size-change="
                ($e: Event) =>
                  handlePageSizeChange(
                    Number(($e.target as HTMLInputElement).value),
                  )
              "
            />
            <cl-table-pagination-state
              :start="rowsIndexPointer.startIndex"
              :end="rowsIndexPointer.endIndex"
              :total="total.total"
              :placeholder="$t('of')"
            />
            <cl-table-basic-pagination
              :previous-page-text="$t('previous-pagination')"
              :next-page-text="$t('next')"
              :can-previous-page="table.getCanPreviousPage()"
              :previous-page="table.previousPage"
              :can-next-page="table.getCanNextPage()"
              :next-page="table.nextPage"
            />
          </div>
        </div>
      </template>
    </cl-table-wrapper>
  </div>
</template>

<script setup lang="ts">
import { useAuthenticationStore } from "@/store/authentication.store";
import { storeToRefs } from "pinia";
import { useOrganisationsStore } from "@/store/organisations.store";
import { useLicenceStore } from "@/modules/Usage/store/licence.store";
import { useAzureStore } from "@/store/azure.store";

import { PHISH_SERVICE } from "@/utils/services";
import TEmpty from "@/components/TEmpty";
import { computed, h, ref } from "vue";
import { useI18n } from "vue-i18n";
import { watch } from "vue";
import { useRouter } from "vue-router";
import { useGlobalOrganisationIDBasedOnRoute } from "@/composables/useGlobalOrganisationIDBasedOnRoute";
import { useFlag } from "@/composables/useFlags/useFlag";
import {
  useRowsIndexPointer,
  useSkeletonArray,
  useServerSidePagination,
  useMemoDefaultParams,
  useMemoColumnVisibility,
  useColumnVisibility,
} from "@/composables/table-helpers";
import { useDebounceFn } from "@vueuse/core";
import {
  useVueTable,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  FlexRender,
  ColumnDef,
} from "@tanstack/vue-table";
import { transformOrganisationsToCustomers } from "./utils/transformOrganisationsToCustomers";
import dayjs from "dayjs";
import { MAX_COLUMN_SIZE } from "@/modules/PhishTitan/constants/data-table";
import TCustomerConnection from "@/components/TCustomerConnection/TCustomerConnection.vue";
import { EMAIL_SECURITY } from "@/authorisation";
import { Customer } from "./types";
import CustomerViewButton from "./CustomerViewButton/CustomerViewButton.vue";
import {
  redirectToConnections as redirectToConnectionsHelper,
  redirectToConnectionsV2 as redirectToConnectionsV2Helper,
} from "@/utils/navigation/redirectToConnections";
import { redirectToCustomerDetails as redirectToCustomerDetailsHelper } from "../navigation";

const { globalOrganisationId } = useGlobalOrganisationIDBasedOnRoute();
const { isFeatureAvailable: isConnectionsV2Available } =
  useFlag("CONNECTIONS_V2");
const { isFeatureAvailable: isAppMigrationEnabled } = useFlag(
  "M365_AZURE_APP_MIGRATION",
);

const loading = ref(true);

const authStore = useAuthenticationStore();
const organisationsStore = useOrganisationsStore();
const {
  orginisationsList: organisationsList,
  organisationsTotal: total,
  organisationsConnections,
} = storeToRefs(organisationsStore);
const licenceStore = useLicenceStore();
const azureStore = useAzureStore();

const customersList = computed(() =>
  transformOrganisationsToCustomers(
    organisationsList.value as unknown as Parameters<
      typeof transformOrganisationsToCustomers
    >[0],
    organisationsConnections.value,
  ),
);

const getOrganisationsList = async () => {
  loading.value = true;

  try {
    await organisationsStore.getOrginisationsList({
      parent_uuid: globalOrganisationId.value,
      params: {
        solution_uuid: PHISH_SERVICE,
        page: paramsState.page,
        results: paramsState.results,
        sortDir: paramsState.sortDir.toLocaleUpperCase() || "ASC",
        sortKey: paramsState.sortKey || "name",
        filter: paramsState.filter || "",
      },
      with_connections: true,
    });
  } finally {
    loading.value = false;
  }
};
const setCustomer = async (payload: { id: string; name: string }) => {
  try {
    await authStore.setCustomer({
      uuid: payload.id,
      name: payload.name,
    });
  } catch {
    // Handled in axios interceptor
  }
};

const clearAzureCache = async () => {
  try {
    await azureStore.clearAzureCache();
  } catch {
    // Handled in axios interceptor
  }
};
const clearLicenceCache = async () => {
  try {
    await licenceStore.clearLicenceCache();
  } catch {
    // Handled in axios interceptor
  }
};

const { t } = useI18n();

const columns: ColumnDef<Customer>[] = [
  {
    header: t("customer"),
    accessorKey: "name",
    size: 260,
    maxSize: MAX_COLUMN_SIZE,
  },
  {
    header: t("description-placeholder"),
    accessorKey: "description",
    size: 320,
    maxSize: MAX_COLUMN_SIZE,
    enableSorting: false,
  },
  {
    header: t("active-users"),
    accessorKey: "seats_used",
    size: 150,
    meta: {
      headerTooltip: t("active-users-tooltip"),
    },
  },
  {
    header: t("licenses-issued"),
    accessorKey: "seats_purchased",
    size: 160,
    meta: {
      headerTooltip: t("email-security-licenses-issued"),
    },
  },
  {
    header: t("date-created"),
    accessorKey: "created_at",
    size: 150,
    cell: ({ getValue }) => dayjs(getValue<Date>()).format("MMM D, YYYY"),
  },
  {
    header: t("connection"),
    accessorKey: "hasConnection",
    size: 150,
    enableSorting: false,
    cell: ({ row, getValue }) => {
      const rowIndex = Number(row.id);
      const { uuid, name, isLegacy } = customersList.value[rowIndex];

      // The legacy-app migration affordance (the "Update permissions" cell and
      // its v2 redirect) is gated behind M365_AZURE_APP_MIGRATION; with the flag
      // off the cell behaves exactly as before P-287.
      const isLegacyMigration = isAppMigrationEnabled.value && !!isLegacy;

      return h(TCustomerConnection, {
        hasConnection: getValue<boolean>(),
        isLegacy: isLegacyMigration,
        authorisationResource: EMAIL_SECURITY,
        onClicked: () =>
          isLegacyMigration
            ? redirectToConnectionsV2({ id: uuid, name })
            : redirectToConnections({ id: uuid, name }),
      });
    },
  },
  {
    header: t("actions"),
    accessorKey: "actions",
    size: 80,
    enableSorting: false,
    cell: ({ row }) => {
      const rowIndex = Number(row.id);
      const { uuid, name } = customersList.value[rowIndex];

      return h(CustomerViewButton, {
        onClicked: () => redirectToCustomerDetails({ id: uuid, name }),
      });
    },
  },
];

const { defaultParams, handleParamsChange } = useMemoDefaultParams(
  `msp-email-security-customers`,
  {
    pageSize: 10,
    sortDir: "asc",
    sortKey: "name",
    filter: "",
  },
);
const { columnVisibility } = useMemoColumnVisibility(
  `msp-email-security-customers-columns`,
);

const {
  paramsState,
  filterState,
  sortingState,
  paginationState,
  handlePageSizeChange,
} = useServerSidePagination({
  pageIndex: 0,
  pageSize: defaultParams.value.pageSize,
  sortDir: defaultParams.value.sortDir,
  sortKey: defaultParams.value.sortKey,
  filter: "",
});

const emptyState = computed(() =>
  paramsState.filter
    ? {
        icon: "cl-icon-empty-folder",
        title: t("no-search-results-default"),
        description: t("subtext-no-search-results-default"),
      }
    : {
        icon: "cl-icon-empty-box",
        title: t("no-search-results-default"),
        description: t("no-customers"),
      },
);

const router = useRouter();

const redirectToConnections = (row: { id: string; name: string }) =>
  redirectToConnectionsHelper(row, {
    router,
    setCustomer,
    isConnectionsV2Available: isConnectionsV2Available.value,
  });

const redirectToConnectionsV2 = (row: { id: string; name: string }) =>
  redirectToConnectionsV2Helper(row, { router, setCustomer });

const redirectToCustomerDetails = (row: { id: string; name: string }) =>
  redirectToCustomerDetailsHelper(row, {
    router,
    setCustomer,
    clearAzureCache,
    clearLicenceCache,
  });

const table = useVueTable({
  get data() {
    return customersList.value;
  },
  get pageCount() {
    return Math.ceil(total.value.total / defaultParams.value.pageSize);
  },
  initialState: {
    columnPinning: {
      right: ["actions"],
    },
  },
  columns: columns,
  getCoreRowModel: getCoreRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  getFilteredRowModel: getFilteredRowModel(),
  getSortedRowModel: getSortedRowModel(),

  onPaginationChange: (updater) => {
    updater instanceof Function
      ? (paginationState.value = updater(paginationState.value))
      : (paginationState.value = updater);
  },
  onSortingChange: (updater) => {
    updater instanceof Function
      ? (sortingState.value = updater(sortingState.value))
      : (sortingState.value = updater);
  },
  state: {
    get pagination() {
      return paginationState.value;
    },
    get sorting() {
      return sortingState.value;
    },
    get columnVisibility() {
      return columnVisibility.value;
    },
  },
  manualPagination: true,
});

const debounceSearch = useDebounceFn((phrase: string) => {
  filterState.value = phrase;
}, 500);
const rowsIndexPointer = useRowsIndexPointer(table);

watch(
  paramsState,
  () => {
    getOrganisationsList();

    handleParamsChange({
      pageSize: paramsState.results,
      sortDir: paramsState.sortDir,
      sortKey: paramsState.sortKey,
      filter: paramsState.filter,
    });
  },
  { immediate: true, deep: true },
);

const skeletonArray = useSkeletonArray(table, 10);
const { columnsToggleList, toggleColumnVisibility } = useColumnVisibility(
  table,
  columnVisibility,
);
</script>
